import React, { useState, useEffect } from 'react';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { validatePhoneNumber } from '../utils/common';

const COUNTRY_OPTIONS: { code: CountryCode; label: string; dialCode: string }[] = [
  { code: 'US', label: 'United States', dialCode: '+1' },
  { code: 'CA', label: 'Canada', dialCode: '+1' },
  { code: 'MX', label: 'Mexico', dialCode: '+52' },
];

interface PhoneNumberInputProps {
  value: string;
  className?: string;
  placeholder?: string;
  onChange: (formatted: string, isValid: boolean) => void;
  defaultCountry?: CountryCode;
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  className,
  placeholder,
  defaultCountry = 'US',
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    COUNTRY_OPTIONS.find((country) => country.code === defaultCountry)?.code || 'US',
  );
  const [error, setError] = useState<string | null>(null);

  const getDigitsOnly = (input: string) => {
    return input.replace(/\D/g, '');
  };

  const getCountryOption = (country: CountryCode) =>
    COUNTRY_OPTIONS.find((option) => option.code === country) || COUNTRY_OPTIONS[0];

  const formatPhone = (input: string, country: CountryCode): string => {
    const digits = getDigitsOnly(input);
    if (!digits) return '';

    const countryOption = getCountryOption(country);
    const dialDigits = getDigitsOnly(countryOption.dialCode);
    let strippedDigits = digits;

    // Avoid duplicating the country code when a user types +1 1...
    if (strippedDigits.startsWith(dialDigits + dialDigits)) {
      strippedDigits = strippedDigits.slice(dialDigits.length);
    }

    const normalizedDigits = strippedDigits.startsWith(dialDigits)
      ? strippedDigits
      : `${dialDigits}${strippedDigits}`;

    const formatter = new AsYouType(country);
    return formatter.input(`+${normalizedDigits}`);
  };

  const runValidation = (input: string, country: CountryCode) => {
    const validationResult = validatePhoneNumber(input, country);
    if (typeof validationResult === 'string') {
      setError(validationResult);
      onChange(input, false);
    } else {
      setError(null);
      onChange(input, true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const digits = getDigitsOnly(rawInput);

    if (!digits) {
      setInternalValue('');
      setError(null);
      onChange('', false);
      return;
    }

    const input = formatPhone(rawInput, selectedCountry);

    setInternalValue(input);
    runValidation(input, selectedCountry);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextCountry = e.target.value as CountryCode;
    setSelectedCountry(nextCountry);

    if (!internalValue) return;

    const reformatted = formatPhone(internalValue, nextCountry);
    setInternalValue(reformatted);
    runValidation(reformatted, nextCountry);
  };

  useEffect(() => {
    const formatted = value ? formatPhone(value, selectedCountry) : '';
    setInternalValue(formatted);
  }, [value, selectedCountry]);

  useEffect(() => {
    const nextCountry = COUNTRY_OPTIONS.find((country) => country.code === defaultCountry)?.code || 'US';
    setSelectedCountry(nextCountry);
  }, [defaultCountry]);

  const selectedOption = getCountryOption(selectedCountry);
  const computedPlaceholder = placeholder || `${selectedOption.dialCode} 555 123 4567`;

  return (
    <div>
      <div className="flex gap-3">
        <div className="w-24">
          <select
            aria-label="Country code"
            value={selectedCountry}
            onChange={handleCountryChange}
            className="w-full p-3 rounded-lg bg-brand-hover border border-gray-700 text-neutral-light focus:border-accent focus:ring-1 focus:ring-accent transition-all duration-200"
          >
            {COUNTRY_OPTIONS.map((country) => (
              <option key={country.code} value={country.code}>
                {country.code}
              </option>
            ))}
          </select>
        </div>
        <input
          type="tel"
          value={internalValue}
          onChange={handleChange}
          placeholder={computedPlaceholder}
          className={`${className} w-full p-3 rounded-lg bg-brand-hover border ${error ? 'border-red-500' : 'border-gray-700'
            } text-neutral-light placeholder-text-muted focus:border-accent focus:ring-1 focus:ring-accent transition-all duration-200`}
          required
        />
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};
